import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { formatINR } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from "@/components/ui/dialog";
import { Clock, Users, Heart, Send, Check, CreditCard, UtensilsCrossed, Lock, Timer, LogIn, LogOut, Download, Play, ExternalLink, X, Instagram, Phone } from "lucide-react";
import jsPDF from "jspdf";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from "recharts";
import AvailabilityChecker from "@/components/AvailabilityChecker";
import ChatWidget from "@/components/ChatWidget";
// Analytics tracking
import analytics, { 
  trackEvent, 
  trackCTA, 
  trackFormStart, 
  trackFormSubmit,
  trackSectionView,
  trackSectionLeave,
  linkLead,
  trackFilterChange
} from "@/utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_HERO_IMAGE = "https://images.unsplash.com/photo-1519741497674-611481863552?w=1920&q=80";
const LOGO_IMAGE = "https://customer-assets.emergentagent.com/job_8ceb28f1-6ce8-4262-b265-11a11581de83/artifacts/69md6nd7_Happy-Retreat-0003.png";

const WeddingsPage = () => {
  // State for inputs
  const [guestCount, setGuestCount] = useState(50);
  const [durationHours, setDurationHours] = useState(24); // Default: 24 hours
  const [advancePayment, setAdvancePayment] = useState(100); // Default: 100% upfront
  const [selectedFoodTier, setSelectedFoodTier] = useState(null);
  const [includeFood, setIncludeFood] = useState(true);
  
  // State for data
  const [venuePricing, setVenuePricing] = useState(null);
  const [foodTiers, setFoodTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // State for calculation result
  const [breakdown, setBreakdown] = useState(null);
  
  // State for Google auth
  const [googleUser, setGoogleUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  // State for deal lock
  const [activeLock, setActiveLock] = useState(null);
  const [lockingDeal, setLockingDeal] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  
  // State for lead capture
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadForm, setLeadForm] = useState({
    name: "",
    phone: "",
    email: "",
    event_date: "",
    notes: ""
  });
  const [submittingLead, setSubmittingLead] = useState(false);

  // State for discount popup
  const [showDiscountPopup, setShowDiscountPopup] = useState(false);
  const previousAdvancePaymentRef = useRef(100); // Default: 100% upfront
  const [animatedDiscount, setAnimatedDiscount] = useState(0);

  // PDF export state
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // YouTube videos state
  const [activeVideo, setActiveVideo] = useState(null);
  const [youtubeVideos, setYoutubeVideos] = useState([]);
  
  // Playlist ID for "View full playlist" link
  const playlistId = "PLYlF1QCFdS4WA73NO1GwF8OKJAxD5oguc";

  // Instagram reels state
  const [instagramReels, setInstagramReels] = useState([]);

  // Performance & Revenue chart data
  const [monthlyPerformance, setMonthlyPerformance] = useState([]);
  const [revenueCategories, setRevenueCategories] = useState([]);
  const [showCharts, setShowCharts] = useState(true);

  // CMS Site Content
  const [siteContent, setSiteContent] = useState({});

  // Availability checker data
  const [availabilityData, setAvailabilityData] = useState(null);
  const [hasCheckedAvailability, setHasCheckedAvailability] = useState(false);
  
  // Mobile detection for responsive media
  const [isMobile, setIsMobile] = useState(false);
  
  // Mobile detection effect - must be at top level with other hooks
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle availability check callback
  const handleAvailabilityChecked = (data) => {
    setAvailabilityData(data);
    setHasCheckedAvailability(true);
  };

  // Session tracking
  const getSessionId = () => {
    let sessionId = localStorage.getItem("availability_session_id");
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem("availability_session_id", sessionId);
    }
    return sessionId;
  };

  const trackLegacyEvent = useCallback(async (eventName, payload = {}) => {
    try {
      await axios.post(`${API}/track-activity`, {
        session_id: getSessionId(),
        event_name: eventName,
        event_payload: payload,
        page_url: window.location.href
      });
    } catch (error) {
      console.error("Failed to track event:", error);
    }
  }, []);

  // Track page view on mount (legacy tracking)
  useEffect(() => {
    trackLegacyEvent("page_view", { page: "weddings" });
  }, [trackLegacyEvent]);

  // PDF Export function - Professional branded estimate
  const handleExportPdf = async () => {
    if (!breakdown) return;
    trackEvent("pdf_download_started");
    
    setGeneratingPdf(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - 2 * margin;
      let yPos = 8; // Start higher to use whitespace
      
      // Brand colors - refined palette
      const brandBrown = { r: 139, g: 90, b: 43 }; // Rich brown
      const accentGold = { r: 176, g: 137, b: 104 }; // #B08968
      const darkText = { r: 33, g: 33, b: 33 };
      const mediumText = { r: 80, g: 80, b: 80 };
      const lightText = { r: 120, g: 120, b: 120 };
      const successGreen = { r: 21, g: 128, b: 61 };
      const lightBg = { r: 250, g: 248, b: 245 };
      const borderColor = { r: 220, g: 215, b: 210 };
      
      // Helper functions
      const formatCurrency = (amount) => `Rs. ${amount.toLocaleString('en-IN')}`;
      const formatDateForPdf = (dateStr) => {
        if (!dateStr) return 'Not specified';
        const date = new Date(dateStr + 'T00:00:00');
        return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
      };
      
      const drawSectionHeader = (title, y) => {
        pdf.setFillColor(brandBrown.r, brandBrown.g, brandBrown.b);
        pdf.rect(margin, y, contentWidth, 7, 'F');
        pdf.setFontSize(9);
        pdf.setTextColor(255, 255, 255);
        pdf.setFont(undefined, 'bold');
        pdf.text(title, margin + 4, y + 5);
        return y + 10;
      };

      // ============ HEADER ============
      // Load logo
      const logoUrl = `${window.location.origin}/happy-retreats-logo.png`;
      try {
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        const logoDataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        const logoWidth = 40;
        const logoHeight = logoWidth / 1.414;
        pdf.addImage(logoDataUrl, 'PNG', (pageWidth - logoWidth) / 2, yPos, logoWidth, logoHeight);
        yPos += logoHeight + 2;
      } catch (e) {
        pdf.setFontSize(16);
        pdf.setTextColor(brandBrown.r, brandBrown.g, brandBrown.b);
        pdf.setFont(undefined, 'bold');
        pdf.text("HAPPY RETREATS", pageWidth / 2, yPos + 5, { align: 'center' });
        yPos += 8;
      }
      
      // Title
      pdf.setFontSize(14);
      pdf.setTextColor(darkText.r, darkText.g, darkText.b);
      pdf.setFont(undefined, 'bold');
      pdf.text("Wedding Budget Estimate", pageWidth / 2, yPos, { align: 'center' });
      yPos += 4;
      
      // Venue name
      pdf.setFontSize(10);
      pdf.setTextColor(accentGold.r, accentGold.g, accentGold.b);
      pdf.text("KAI - Celebration Venue", pageWidth / 2, yPos, { align: 'center' });
      yPos += 4;
      
      // Date
      pdf.setFontSize(7);
      pdf.setTextColor(lightText.r, lightText.g, lightText.b);
      pdf.setFont(undefined, 'normal');
      const dateStr = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
      pdf.text(`Generated: ${dateStr}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 4;
      
      // Divider
      pdf.setDrawColor(accentGold.r, accentGold.g, accentGold.b);
      pdf.setLineWidth(0.5);
      pdf.line(margin + 50, yPos, pageWidth - margin - 50, yPos);
      yPos += 5;

      // ============ CUSTOMER & EVENT DETAILS ============
      yPos = drawSectionHeader("BOOKING DETAILS", yPos);
      
      const bookingBoxHeight = 24;
      pdf.setFillColor(lightBg.r, lightBg.g, lightBg.b);
      pdf.roundedRect(margin, yPos, contentWidth, bookingBoxHeight, 2, 2, 'F');
      
      const leftCol = margin + 6;
      const rightCol = pageWidth / 2 + 6;
      
      pdf.setFontSize(7);
      // Left column - Row 1
      pdf.setTextColor(lightText.r, lightText.g, lightText.b);
      pdf.text("Guest Name", leftCol, yPos + 5);
      pdf.setTextColor(darkText.r, darkText.g, darkText.b);
      pdf.setFont(undefined, 'bold');
      pdf.text(availabilityData?.customerName || 'Not provided', leftCol, yPos + 10);
      
      // Left column - Row 2
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(lightText.r, lightText.g, lightText.b);
      pdf.text("Contact", leftCol, yPos + 15);
      pdf.setTextColor(darkText.r, darkText.g, darkText.b);
      pdf.text(availabilityData?.phoneNumber || 'Not provided', leftCol, yPos + 20);
      
      // Right column - Row 1
      pdf.setTextColor(lightText.r, lightText.g, lightText.b);
      pdf.text("Check-in Date", rightCol, yPos + 5);
      pdf.setTextColor(darkText.r, darkText.g, darkText.b);
      pdf.setFont(undefined, 'bold');
      pdf.text(formatDateForPdf(availabilityData?.checkIn), rightCol, yPos + 10);
      
      // Right column - Row 2
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(lightText.r, lightText.g, lightText.b);
      pdf.text("Check-out Date", rightCol, yPos + 15);
      pdf.setTextColor(darkText.r, darkText.g, darkText.b);
      pdf.text(formatDateForPdf(availabilityData?.checkOut), rightCol, yPos + 20);
      
      yPos += bookingBoxHeight + 5;

      // ============ PACKAGE SELECTIONS ============
      yPos = drawSectionHeader("YOUR SELECTIONS", yPos);
      
      // Selection boxes in a row
      const boxWidth = (contentWidth - 9) / 4;
      const boxHeight = 18;
      const selections = [
        { label: "Duration", value: `${durationHours} Hours` },
        { label: "Guests", value: `${guestCount} Pax` },
        { label: "Food Package", value: breakdown.food.included ? (breakdown.food.tier_name || 'Selected') : 'Opted Out' },
        { label: "Payment", value: getPaymentModeLabel(breakdown.discount.payment_mode) }
      ];
      
      selections.forEach((sel, i) => {
        const x = margin + i * (boxWidth + 3);
        pdf.setFillColor(lightBg.r, lightBg.g, lightBg.b);
        pdf.roundedRect(x, yPos, boxWidth, boxHeight, 2, 2, 'F');
        pdf.setDrawColor(borderColor.r, borderColor.g, borderColor.b);
        pdf.setLineWidth(0.3);
        pdf.roundedRect(x, yPos, boxWidth, boxHeight, 2, 2, 'S');
        
        // Label - centered at top of box
        pdf.setFontSize(6);
        pdf.setTextColor(lightText.r, lightText.g, lightText.b);
        pdf.setFont(undefined, 'normal');
        pdf.text(sel.label, x + boxWidth/2, yPos + 5, { align: 'center' });
        
        // Value - centered in middle of box
        pdf.setFontSize(8);
        pdf.setTextColor(darkText.r, darkText.g, darkText.b);
        pdf.setFont(undefined, 'bold');
        pdf.text(sel.value, x + boxWidth/2, yPos + 12, { align: 'center' });
        pdf.setFont(undefined, 'normal');
      });
      
      yPos += boxHeight + 5;

      // ============ COST BREAKDOWN TABLE ============
      yPos = drawSectionHeader("COST BREAKDOWN", yPos);
      
      // Table with fixed column widths
      const tableX = margin;
      const tableWidth = contentWidth;
      const itemColX = tableX + 4;
      const detailsColX = tableX + 50;
      const amountColX = tableX + tableWidth - 4;
      
      // Header row
      const headerHeight = 7;
      pdf.setFillColor(245, 243, 240);
      pdf.rect(tableX, yPos, tableWidth, headerHeight, 'F');
      pdf.setFontSize(7);
      pdf.setTextColor(mediumText.r, mediumText.g, mediumText.b);
      pdf.setFont(undefined, 'bold');
      pdf.text("ITEM", itemColX, yPos + 5);
      pdf.text("DETAILS", detailsColX, yPos + 5);
      pdf.text("AMOUNT", amountColX, yPos + 5, { align: 'right' });
      yPos += headerHeight + 5;
      
      // Venue row
      pdf.setFontSize(8);
      pdf.setTextColor(darkText.r, darkText.g, darkText.b);
      pdf.setFont(undefined, 'normal');
      pdf.text("Venue Package", itemColX, yPos);
      pdf.setTextColor(mediumText.r, mediumText.g, mediumText.b);
      pdf.text(`${durationHours} hours, all-inclusive`, detailsColX, yPos);
      pdf.setTextColor(darkText.r, darkText.g, darkText.b);
      pdf.text(formatCurrency(breakdown.venue.base_price), amountColX, yPos, { align: 'right' });
      yPos += 5;
      
      // Separator after venue
      pdf.setDrawColor(borderColor.r, borderColor.g, borderColor.b);
      pdf.setLineWidth(0.2);
      pdf.line(tableX, yPos, tableX + tableWidth, yPos);
      yPos += 5;
      
      // Food row
      if (breakdown.food.included && breakdown.food.total > 0) {
        const foodName = breakdown.food.tier_name || 'Food Package';
        pdf.setTextColor(darkText.r, darkText.g, darkText.b);
        pdf.text(foodName, itemColX, yPos);
        
        const minPax = breakdown.food.min_guarantee;
        const baseRate = breakdown.food.base_rate;
        const days = breakdown.food.days;
        const additionalPax = breakdown.food.additional_pax || 0;
        
        pdf.setTextColor(mediumText.r, mediumText.g, mediumText.b);
        if (additionalPax > 0) {
          pdf.text(`${minPax} + ${additionalPax} pax × ${days} days`, detailsColX, yPos);
        } else {
          pdf.text(`${minPax} pax × Rs.${baseRate.toLocaleString('en-IN')} × ${days} days`, detailsColX, yPos);
        }
        pdf.setTextColor(darkText.r, darkText.g, darkText.b);
        pdf.text(formatCurrency(breakdown.food.total), amountColX, yPos, { align: 'right' });
        yPos += 5;
        pdf.line(tableX, yPos, tableX + tableWidth, yPos);
        yPos += 5;
      }
      
      // Extra Stay row
      if (breakdown.venue.extra_accommodation > 0) {
        pdf.setTextColor(darkText.r, darkText.g, darkText.b);
        pdf.text("Extra Accommodation", itemColX, yPos);
        pdf.setTextColor(mediumText.r, mediumText.g, mediumText.b);
        pdf.text(`${breakdown.venue.extra_pax} pax × Rs.1,000 × ${breakdown.venue.days} days`, detailsColX, yPos);
        pdf.setTextColor(darkText.r, darkText.g, darkText.b);
        pdf.text(formatCurrency(breakdown.venue.extra_accommodation), amountColX, yPos, { align: 'right' });
        yPos += 5;
        pdf.line(tableX, yPos, tableX + tableWidth, yPos);
        yPos += 3;
      }
      
      // Total row
      const totalRowHeight = 8;
      pdf.setFillColor(brandBrown.r, brandBrown.g, brandBrown.b);
      pdf.rect(tableX, yPos, tableWidth, totalRowHeight, 'F');
      pdf.setFontSize(9);
      pdf.setTextColor(255, 255, 255);
      pdf.setFont(undefined, 'bold');
      pdf.text("TOTAL DEAL VALUE", itemColX, yPos + 5.5);
      pdf.text(formatCurrency(breakdown.deal_value), amountColX, yPos + 5.5, { align: 'right' });
      yPos += totalRowHeight + 5;

      // ============ SAVINGS HIGHLIGHT ============
      const savingsBoxHeight = 18;
      pdf.setFillColor(236, 253, 245);
      pdf.roundedRect(margin, yPos, contentWidth, savingsBoxHeight, 2, 2, 'F');
      pdf.setDrawColor(34, 197, 94);
      pdf.setLineWidth(0.4);
      pdf.roundedRect(margin, yPos, contentWidth, savingsBoxHeight, 2, 2, 'S');
      
      pdf.setFontSize(9);
      pdf.setTextColor(successGreen.r, successGreen.g, successGreen.b);
      pdf.setFont(undefined, 'bold');
      pdf.text("YOUR SAVINGS", margin + 6, yPos + 7);
      
      pdf.setFontSize(8);
      pdf.setFont(undefined, 'normal');
      pdf.text(`With ${getPaymentModeLabel(breakdown.discount.payment_mode)}, you get ${breakdown.discount.percent}% discount`, margin + 6, yPos + 13);
      
      pdf.setFont(undefined, 'bold');
      pdf.setFontSize(11);
      pdf.text(`- ${formatCurrency(breakdown.discount.amount)}`, pageWidth - margin - 6, yPos + 10, { align: 'right' });
      yPos += savingsBoxHeight + 5;

      // ============ PAYMENT OPTIONS TABLE ============
      pdf.setFontSize(8);
      pdf.setTextColor(darkText.r, darkText.g, darkText.b);
      pdf.setFont(undefined, 'bold');
      pdf.text("The following discounts apply based on your chosen mode of payment:", margin, yPos);
      yPos += 6;
      
      const paymentOptions = [
        { mode: 100, percent: 35, label: "100% Advance" },
        { mode: 75, percent: 25, label: "75% Advance" },
        { mode: 50, percent: 10, label: "50% Advance" }
      ];
      
      const dealValue = breakdown.deal_value;
      
      // Table header
      const pmHeaderHeight = 7;
      pdf.setFillColor(245, 243, 240);
      pdf.rect(margin, yPos, contentWidth, pmHeaderHeight, 'F');
      pdf.setFontSize(7);
      pdf.setTextColor(mediumText.r, mediumText.g, mediumText.b);
      pdf.setFont(undefined, 'bold');
      
      // Fixed column positions for payment table
      const pmCol1 = margin + 4;
      const pmCol2 = margin + 50;
      const pmCol3 = margin + 80;
      const pmCol4 = pageWidth - margin - 4;
      
      pdf.text("PAYMENT MODE", pmCol1, yPos + 5);
      pdf.text("DISCOUNT", pmCol2, yPos + 5);
      pdf.text("YOU SAVE", pmCol3, yPos + 5);
      pdf.text("FINAL AMOUNT", pmCol4, yPos + 5, { align: 'right' });
      yPos += pmHeaderHeight + 5;
      
      // Payment rows
      paymentOptions.forEach((option, index) => {
        const discountAmount = Math.round(dealValue * (option.percent / 100));
        const finalAmount = dealValue - discountAmount;
        const isSelected = breakdown.discount.payment_mode === option.mode;
        
        if (isSelected) {
          pdf.setFillColor(236, 253, 245);
          pdf.rect(margin, yPos - 3, contentWidth, 8, 'F');
        }
        
        pdf.setFontSize(8);
        pdf.setTextColor(darkText.r, darkText.g, darkText.b);
        pdf.setFont(undefined, isSelected ? 'bold' : 'normal');
        
        pdf.text(option.label + (isSelected ? " ✓" : ""), pmCol1, yPos);
        pdf.text(`${option.percent}%`, pmCol2, yPos);
        
        pdf.setTextColor(successGreen.r, successGreen.g, successGreen.b);
        pdf.text(formatCurrency(discountAmount), pmCol3, yPos);
        
        pdf.setTextColor(darkText.r, darkText.g, darkText.b);
        pdf.setFont(undefined, 'bold');
        pdf.text(formatCurrency(finalAmount), pmCol4, yPos, { align: 'right' });
        pdf.setFont(undefined, 'normal');
        
        yPos += 5;
        
        if (index < paymentOptions.length - 1) {
          pdf.setDrawColor(borderColor.r, borderColor.g, borderColor.b);
          pdf.setLineWidth(0.2);
          pdf.line(margin, yPos, pageWidth - margin, yPos);
          yPos += 5;
        }
      });
      
      yPos += 4;
      pdf.setFontSize(6);
      pdf.setTextColor(lightText.r, lightText.g, lightText.b);
      pdf.setFont(undefined, 'italic');
      pdf.text("* Higher advance = Higher discount. Discounts apply on Deal Value only.", margin, yPos);
      yPos += 6;

      // ============ GRAND TOTAL BOX ============
      const grandTotalHeight = 14;
      pdf.setFillColor(brandBrown.r, brandBrown.g, brandBrown.b);
      pdf.roundedRect(margin, yPos, contentWidth, grandTotalHeight, 2, 2, 'F');
      
      pdf.setFontSize(10);
      pdf.setTextColor(255, 255, 255);
      pdf.setFont(undefined, 'bold');
      pdf.text("AMOUNT PAYABLE", margin + 6, yPos + 9);
      
      pdf.setFontSize(12);
      pdf.text(formatCurrency(breakdown.grand_total), pageWidth - margin - 6, yPos + 9.5, { align: 'right' });

      // ============ PAGE 2: FOUNDER'S NOTE + T&C ============
      pdf.addPage();
      yPos = 15;
      
      // Founder's Note - elegant design
      pdf.setDrawColor(accentGold.r, accentGold.g, accentGold.b);
      pdf.setLineWidth(0.4);
      pdf.line(margin + 50, yPos, pageWidth - margin - 50, yPos);
      yPos += 8;
      
      pdf.setFontSize(13);
      pdf.setTextColor(brandBrown.r, brandBrown.g, brandBrown.b);
      pdf.setFont(undefined, 'bold');
      pdf.text("A note from the Founder", pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
      
      // Opening quote
      pdf.setFontSize(10);
      pdf.setTextColor(darkText.r, darkText.g, darkText.b);
      pdf.setFont(undefined, 'bolditalic');
      const openingText = "Your wedding is not just a celebration. It's your parents' dream coming to life.";
      pdf.text(openingText, pageWidth / 2, yPos, { align: 'center' });
      yPos += 12;
      
      // Body text
      pdf.setFontSize(9);
      pdf.setFont(undefined, 'italic');
      pdf.setTextColor(mediumText.r, mediumText.g, mediumText.b);
      
      const founderText1 = "At KAI, we understand the weight of that trust. For these few days, we don't act like a venue team — we step in as your extended family. The ones who stay calm, think ahead, and quietly take responsibility so your parents can breathe easy and actually enjoy the wedding.";
      const lines1 = pdf.splitTextToSize(founderText1, contentWidth - 20);
      pdf.text(lines1, pageWidth / 2, yPos, { align: 'center' });
      yPos += lines1.length * 5 + 6;
      
      const founderText2 = "We keep things simple, transparent, and unrushed. We take on fewer weddings because we believe presence matters more than volume.";
      const lines2 = pdf.splitTextToSize(founderText2, contentWidth - 20);
      pdf.text(lines2, pageWidth / 2, yPos, { align: 'center' });
      yPos += lines2.length * 5 + 8;
      
      // Quote highlight
      pdf.setFontSize(9);
      pdf.setTextColor(accentGold.r, accentGold.g, accentGold.b);
      pdf.setFont(undefined, 'bolditalic');
      pdf.text("\"Parents often tell us, 'It felt like family was taking care of us.'\"", pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
      
      pdf.setFontSize(9);
      pdf.setFont(undefined, 'italic');
      pdf.setTextColor(mediumText.r, mediumText.g, mediumText.b);
      pdf.text("That's the feeling we work towards every single time.", pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
      
      // Signature
      pdf.setFontSize(9);
      pdf.setTextColor(darkText.r, darkText.g, darkText.b);
      pdf.setFont(undefined, 'normal');
      pdf.text("Warm regards,", pageWidth / 2, yPos, { align: 'center' });
      yPos += 7;
      
      pdf.setFontSize(11);
      pdf.setTextColor(brandBrown.r, brandBrown.g, brandBrown.b);
      pdf.setFont(undefined, 'bold');
      pdf.text("Pradeep Gowriraju", pageWidth / 2, yPos, { align: 'center' });
      yPos += 6;
      
      pdf.setFontSize(8);
      pdf.setTextColor(lightText.r, lightText.g, lightText.b);
      pdf.setFont(undefined, 'normal');
      pdf.text("Founder & CEO, Happy Retreats", pageWidth / 2, yPos, { align: 'center' });
      yPos += 12;
      
      // Divider
      pdf.setDrawColor(accentGold.r, accentGold.g, accentGold.b);
      pdf.line(margin + 50, yPos, pageWidth - margin - 50, yPos);
      yPos += 10;

      // ============ TERMS & CONDITIONS ============
      yPos = drawSectionHeader("TERMS & CONDITIONS", yPos);
      
      pdf.setFontSize(8);
      pdf.setTextColor(mediumText.r, mediumText.g, mediumText.b);
      pdf.setFont(undefined, 'normal');
      
      const terms = [
        "This estimate is indicative and based on the selections made.",
        "All prices are inclusive of GST unless stated otherwise.",
        "Discounts apply on Deal Value only, not on extra accommodation charges.",
        "Prices may change unless the deal is locked for 48 hours through the platform.",
        "Booking is confirmed only upon receipt of advance and written confirmation from Happy Retreats.",
        "This price quote is valid for 48 hours from the date of generation."
      ];
      
      terms.forEach((term, i) => {
        pdf.text(`${i + 1}. ${term}`, margin + 5, yPos);
        yPos += 6;
      });
      yPos += 8;

      // ============ BANK DETAILS ============
      yPos = drawSectionHeader("BANK DETAILS FOR PAYMENT", yPos);
      
      const bankBoxHeight = 35;
      pdf.setFillColor(lightBg.r, lightBg.g, lightBg.b);
      pdf.roundedRect(margin, yPos, contentWidth, bankBoxHeight, 2, 2, 'F');
      
      pdf.setFontSize(8);
      const bankDetails = [
        { label: "Account Name:", value: "Stayuntapd Conceptual Retreats LLP" },
        { label: "Account Number:", value: "4749139809" },
        { label: "IFSC Code:", value: "KKBK0008060" },
        { label: "Account Type:", value: "Current" },
        { label: "Branch:", value: "Kotak Mahindra Sadashivanagar" }
      ];
      
      // Left column (first 3 items)
      const bankLeftCol = margin + 8;
      const bankRightCol = pageWidth / 2 + 5;
      const bankLabelWidth = 32;
      
      bankDetails.slice(0, 3).forEach((detail, i) => {
        const y = yPos + 8 + i * 8;
        pdf.setTextColor(lightText.r, lightText.g, lightText.b);
        pdf.setFont(undefined, 'normal');
        pdf.text(detail.label, bankLeftCol, y);
        pdf.setTextColor(darkText.r, darkText.g, darkText.b);
        pdf.setFont(undefined, 'bold');
        pdf.text(detail.value, bankLeftCol + bankLabelWidth, y);
      });
      
      // Right column (last 2 items)
      bankDetails.slice(3).forEach((detail, i) => {
        const y = yPos + 8 + i * 8;
        pdf.setTextColor(lightText.r, lightText.g, lightText.b);
        pdf.setFont(undefined, 'normal');
        pdf.text(detail.label, bankRightCol, y);
        pdf.setTextColor(darkText.r, darkText.g, darkText.b);
        pdf.setFont(undefined, 'bold');
        pdf.text(detail.value, bankRightCol + bankLabelWidth, y);
      });
      
      yPos += bankBoxHeight + 10;

      // ============ FOOTER ============
      pdf.setDrawColor(accentGold.r, accentGold.g, accentGold.b);
      pdf.setLineWidth(0.3);
      pdf.line(margin + 30, yPos, pageWidth - margin - 30, yPos);
      yPos += 10;
      
      pdf.setFontSize(10);
      pdf.setTextColor(brandBrown.r, brandBrown.g, brandBrown.b);
      pdf.setFont(undefined, 'bold');
      pdf.text("Happy Retreats - KAI", pageWidth / 2, yPos, { align: 'center' });
      yPos += 6;
      
      pdf.setFontSize(9);
      pdf.setTextColor(accentGold.r, accentGold.g, accentGold.b);
      pdf.setFont(undefined, 'normal');
      pdf.text("happyretreats.in", pageWidth / 2, yPos, { align: 'center' });
      yPos += 6;
      
      pdf.setFontSize(8);
      pdf.setTextColor(lightText.r, lightText.g, lightText.b);
      pdf.text("Transparent pricing. No hidden costs.", pageWidth / 2, yPos, { align: 'center' });
      
      // Save PDF
      const fileName = `HappyRetreats_KAI_Estimate_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      trackEvent("pdf_downloaded", { total: breakdown?.grand_total });
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("PDF generation failed:", error);
      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Check for session_id in URL hash (Google auth callback)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) {
        processGoogleSession(sessionId);
      }
    } else {
      checkGoogleAuth();
    }
  }, []);

  const processGoogleSession = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/google/session`, 
        { session_id: sessionId },
        { withCredentials: true }
      );
      setGoogleUser(response.data);
      // Clear the hash from URL
      window.history.replaceState(null, '', window.location.pathname);
      toast.success(`Welcome, ${response.data.name}!`);
    } catch (error) {
      console.error("Google auth failed:", error);
      toast.error("Sign in failed. Please try again.");
    } finally {
      setCheckingAuth(false);
    }
  };

  const checkGoogleAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/google/me`, { withCredentials: true });
      setGoogleUser(response.data);
      checkActiveLock();
    } catch (error) {
      // Not authenticated - that's fine
    } finally {
      setCheckingAuth(false);
    }
  };

  const checkActiveLock = async () => {
    try {
      const response = await axios.get(`${API}/deals/active-lock`, { withCredentials: true });
      if (response.data.has_lock) {
        setActiveLock(response.data.lock);
        setTimeRemaining(response.data.time_remaining_seconds);
      } else if (response.data.expired) {
        toast.info("Your previous price lock has expired. Please recalculate to continue.");
      }
    } catch (error) {
      console.error("Failed to check lock:", error);
    }
  };

  // Countdown timer for active lock
  useEffect(() => {
    if (timeRemaining && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setActiveLock(null);
            toast.info("Your price lock has expired. Please recalculate to continue.");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timeRemaining]);

  // Discount popup effect when payment mode changes to 75% or 100%
  useEffect(() => {
    const previousAdvancePayment = previousAdvancePaymentRef.current;
    
    // Only show popup when advancing TO a higher payment mode (50->75, 50->100, 75->100)
    // NOT when going down (100->75, 100->50, 75->50)
    const isAdvancingPayment = previousAdvancePayment < advancePayment;
    const isHigherPaymentMode = advancePayment === 75 || advancePayment === 100;
    
    // Update the ref immediately to prevent re-triggering
    previousAdvancePaymentRef.current = advancePayment;
    
    if (isHigherPaymentMode && isAdvancingPayment) {
      // Delay showing popup until breakdown is available
      const showPopupTimer = setTimeout(() => {
        if (breakdown && breakdown.discount.amount > 0) {
          setShowDiscountPopup(true);
          
          // Animate the discount amount counting up
          const targetDiscount = breakdown.discount.amount;
          const duration = 800; // ms
          const steps = 30;
          const increment = targetDiscount / steps;
          let current = 0;
          
          const animationInterval = setInterval(() => {
            current += increment;
            if (current >= targetDiscount) {
              setAnimatedDiscount(targetDiscount);
              clearInterval(animationInterval);
            } else {
              setAnimatedDiscount(Math.round(current));
            }
          }, duration / steps);
          
          // Auto-dismiss after 3.5 seconds
          const dismissTimer = setTimeout(() => {
            setShowDiscountPopup(false);
          }, 3500);
          
          // Store cleanup functions
          return () => {
            clearInterval(animationInterval);
            clearTimeout(dismissTimer);
          };
        }
      }, 100); // Small delay to wait for breakdown to update
      
      return () => clearTimeout(showPopupTimer);
    } else if (advancePayment === 50) {
      // Remove instantly when switching back to 50%
      setShowDiscountPopup(false);
      setAnimatedDiscount(0);
    }
  }, [advancePayment]);
  
  // Update animated discount when breakdown changes (for already visible popup)
  useEffect(() => {
    if (showDiscountPopup && breakdown && breakdown.discount.amount > 0) {
      setAnimatedDiscount(breakdown.discount.amount);
    }
  }, [breakdown, showDiscountPopup]);

  const formatTimeRemaining = (seconds) => {
    if (!seconds || seconds <= 0) return "Expired";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours}h ${minutes}m ${secs}s`;
  };

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/weddings';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoogleSignOut = async () => {
    try {
      await axios.post(`${API}/auth/google/logout`, {}, { withCredentials: true });
      setGoogleUser(null);
      setActiveLock(null);
      toast.success("Signed out successfully");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleLockDeal = async () => {
    if (!googleUser) {
      toast.error("Please sign in with Google to lock this price");
      trackEvent("lock_deal_auth_required", { has_breakdown: !!breakdown });
      return;
    }
    
    if (!breakdown) return;
    
    setLockingDeal(true);
    trackEvent("lock_deal_started", { 
      deal_value: breakdown.deal_value,
      grand_total: breakdown.grand_total 
    });
    
    try {
      const response = await axios.post(`${API}/deals/lock`, {
        inputs_snapshot: {
          duration_hours: durationHours,
          guest_count: guestCount,
          food_included: includeFood,
          food_tier_id: selectedFoodTier,
          payment_mode: advancePayment
        },
        breakdown_snapshot: breakdown,
        locked_deal_value: breakdown.deal_value,
        grand_total: breakdown.grand_total,
        check_in: availabilityData?.checkIn || null,
        check_out: availabilityData?.checkOut || null
      }, { withCredentials: true });
      
      toast.success("Price locked for 48 hours!");
      trackEvent("lock_deal_success", { 
        deal_value: breakdown.deal_value,
        grand_total: breakdown.grand_total,
        duration_hours: durationHours
      });
      checkActiveLock();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to lock deal");
      trackEvent("lock_deal_failed", { 
        error: error.response?.data?.detail || "Unknown error"
      });
    } finally {
      setLockingDeal(false);
    }
  };

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed-data`);
        
        const [venueRes, tiersRes] = await Promise.all([
          axios.get(`${API}/venue-pricing`),
          axios.get(`${API}/food-tiers`)
        ]);
        
        setVenuePricing(venueRes.data);
        setFoodTiers(tiersRes.data.filter(t => t.is_active));
        
        if (tiersRes.data.length > 0) {
          // Default to 50 Pax food tier
          const defaultTier = tiersRes.data.find(t => t.min_guarantee === 50) || tiersRes.data[0];
          setSelectedFoodTier(defaultTier.id);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
        toast.error("Failed to load pricing data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Fetch YouTube videos
  useEffect(() => {
    const fetchYouTubeVideos = async () => {
      try {
        const response = await axios.get(`${API}/youtube-videos`);
        if (response.data && response.data.length > 0) {
          setYoutubeVideos(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch YouTube videos:", error);
        // Use fallback videos on error
        setYoutubeVideos([]);
      }
    };
    
    fetchYouTubeVideos();
  }, []);

  // Fetch Instagram reels
  useEffect(() => {
    const fetchInstagramReels = async () => {
      try {
        const response = await axios.get(`${API}/instagram-reels`);
        if (response.data && response.data.length > 0) {
          setInstagramReels(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch Instagram reels:", error);
        setInstagramReels([]);
      }
    };
    
    fetchInstagramReels();
  }, []);

  // Fetch chart data
  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const [perfRes, revRes, settingsRes] = await Promise.all([
          axios.get(`${API}/monthly-performance`),
          axios.get(`${API}/revenue-categories`),
          axios.get(`${API}/chart-settings`)
        ]);
        if (perfRes.data) setMonthlyPerformance(perfRes.data);
        if (revRes.data) setRevenueCategories(revRes.data);
        if (settingsRes.data) setShowCharts(settingsRes.data.show_charts ?? true);
      } catch (error) {
        console.error("Failed to fetch chart data:", error);
      }
    };
    
    fetchChartData();
  }, []);

  // Fetch CMS site content
  useEffect(() => {
    const fetchSiteContent = async () => {
      try {
        const response = await axios.get(`${API}/site-content`);
        if (response.data) {
          setSiteContent(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch site content:", error);
        // Content will fall back to hardcoded defaults
      }
    };
    
    fetchSiteContent();
  }, []);

  // Update SEO meta tags when site content changes
  useEffect(() => {
    if (Object.keys(siteContent).length > 0) {
      // Update document title
      const siteTitle = siteContent.seo_site_title || "Wedding Budget Calculator | Happy Retreats KAI";
      document.title = siteTitle;
      
      // Update meta description
      const metaDescription = siteContent.seo_meta_description || "Plan your dream wedding at KAI by Happy Retreats.";
      let descTag = document.querySelector('meta[name="description"]');
      if (!descTag) {
        descTag = document.createElement('meta');
        descTag.name = 'description';
        document.head.appendChild(descTag);
      }
      descTag.content = metaDescription;
      
      // Update meta keywords
      const metaKeywords = siteContent.seo_meta_keywords || "";
      if (metaKeywords) {
        let keywordsTag = document.querySelector('meta[name="keywords"]');
        if (!keywordsTag) {
          keywordsTag = document.createElement('meta');
          keywordsTag.name = 'keywords';
          document.head.appendChild(keywordsTag);
        }
        keywordsTag.content = metaKeywords;
      }
      
      // Update Open Graph tags for social sharing
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.content = siteTitle;
      
      let ogDesc = document.querySelector('meta[property="og:description"]');
      if (!ogDesc) {
        ogDesc = document.createElement('meta');
        ogDesc.setAttribute('property', 'og:description');
        document.head.appendChild(ogDesc);
      }
      ogDesc.content = metaDescription;
    }
  }, [siteContent]);

  // Helper to get content with fallback
  const getContent = (key, fallback = "") => {
    return siteContent[key] || fallback;
  };

  // Calculate budget whenever inputs change
  useEffect(() => {
    if (!venuePricing) return;
    
    const calculateBudget = async () => {
      try {
        const response = await axios.post(`${API}/calculate-budget`, {
          duration_hours: durationHours,
          advance_payment_percent: advancePayment,
          guest_count: guestCount,
          food_tier_id: includeFood ? selectedFoodTier : null,
          include_food: includeFood
        });
        setBreakdown(response.data);
      } catch (error) {
        console.error("Calculation failed:", error);
      }
    };
    
    calculateBudget();
  }, [durationHours, advancePayment, guestCount, selectedFoodTier, includeFood, venuePricing]);

  // Track lead modal open
  useEffect(() => {
    if (showLeadModal) {
      trackFormStart("lead_capture", "Lead Form");
      trackEvent("lead_modal_opened", {
        grand_total: breakdown?.grand_total,
        duration_hours: durationHours
      });
    }
  }, [showLeadModal, breakdown?.grand_total, durationHours]);

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!breakdown) return;
    
    setSubmittingLead(true);
    trackFormSubmit("lead_capture", "Lead Form", true);
    
    try {
      const response = await axios.post(`${API}/leads`, {
        ...leadForm,
        estimate_snapshot: breakdown,
        inputs_snapshot: {
          duration_hours: durationHours,
          advance_payment: advancePayment,
          guest_count: guestCount,
          food_tier_id: selectedFoodTier,
          include_food: includeFood
        }
      });
      
      // Link lead to analytics session
      if (response.data?.id) {
        linkLead(response.data.id, { 
          name: leadForm.name, 
          email: leadForm.email 
        });
      }
      
      trackEvent("lead_captured", {
        grand_total: breakdown.grand_total,
        duration_hours: durationHours,
        guest_count: guestCount
      });
      
      toast.success("Your estimate has been saved! We'll contact you soon.");
      setShowLeadModal(false);
      setLeadForm({ name: "", phone: "", email: "", event_date: "", notes: "" });
    } catch (error) {
      trackFormSubmit("lead_capture", "Lead Form", false, error.message);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setSubmittingLead(false);
    }
  };

  const getPaymentModeLabel = (percent) => {
    if (percent === 100) return "100% upfront";
    if (percent === 75) return "75% upfront";
    if (percent === 50) return "50% advance";
    return "None";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFCF8]">
        <div className="text-center">
          <div className="loading-shimmer w-16 h-16 rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your wedding planner...</p>
        </div>
      </div>
    );
  }

  const days = durationHours / 24;

  // Get hero media from CMS - separate settings for desktop and mobile
  const heroMediaTypeDesktop = getContent("hero_media_type", "image"); // Desktop: "image" or "video"
  const heroMediaTypeMobile = getContent("hero_media_type_mobile", "image"); // Mobile: "image" or "video"
  
  // Desktop media URLs
  const heroImageUrlDesktop = getContent("hero_image_url", "") || DEFAULT_HERO_IMAGE;
  const heroVideoUrlDesktop = getContent("hero_video_url", "");
  
  // Mobile media URLs (fallback to desktop if not set)
  const heroImageUrlMobile = getContent("hero_image_url_mobile", "") || heroImageUrlDesktop;
  const heroVideoUrlMobile = getContent("hero_video_url_mobile", "") || heroVideoUrlDesktop;
  
  // Use appropriate media type and URL based on viewport
  const heroMediaType = isMobile ? heroMediaTypeMobile : heroMediaTypeDesktop;
  const heroImageUrl = isMobile ? heroImageUrlMobile : heroImageUrlDesktop;
  const heroVideoUrl = isMobile ? heroVideoUrlMobile : heroVideoUrlDesktop;

  const heroOverlayOpacity = parseInt(getContent("hero_overlay_opacity", "30")) || 30; // 0-100
  const heroTextColor = getContent("hero_text_color", "dark"); // "dark" or "light"
  const heroLogoUrl = getContent("hero_logo_url", "") || LOGO_IMAGE; // Custom logo or default
  
  // Text color classes based on CMS setting
  const titleColorClass = heroTextColor === "light" ? "text-white" : "text-[#2D2D2D]";
  const subtitleColorClass = heroTextColor === "light" ? "text-white/90" : "text-[#4A4A4A]";
  const overlayColorClass = heroTextColor === "light" ? "bg-black" : "bg-white";

  // Helper function to extract YouTube video ID from various URL formats
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    // Handle youtube.com/shorts/ID format
    const shortsMatch = url.match(/youtube\.com\/shorts\/([^?&]+)/);
    if (shortsMatch) return shortsMatch[1];
    // Handle youtube.com/watch?v=ID format
    const watchMatch = url.match(/youtube\.com\/watch\?v=([^&]+)/);
    if (watchMatch) return watchMatch[1];
    // Handle youtu.be/ID format
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) return shortMatch[1];
    // Handle youtube.com/embed/ID format
    const embedMatch = url.match(/youtube\.com\/embed\/([^?&]+)/);
    if (embedMatch) return embedMatch[1];
    return null;
  };

  // Check if video URL is a YouTube link
  const isYouTubeUrl = (url) => {
    if (!url) return false;
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  const youtubeVideoId = isYouTubeUrl(heroVideoUrl) ? getYouTubeVideoId(heroVideoUrl) : null;

  return (
    <div className="min-h-screen bg-[#FFFCF8]">
      {/* Hero Section */}
      <section 
        className="hero-section relative flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background - Video or Image */}
        {heroMediaType === "video" && heroVideoUrl ? (
          youtubeVideoId ? (
            // YouTube embed for YouTube URLs - muted, autoplay, loop
            <div className="absolute inset-0 w-full h-full overflow-hidden">
              <iframe
                src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&mute=1&loop=1&playlist=${youtubeVideoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&disablekb=1&fs=0&iv_load_policy=3`}
                className="absolute top-1/2 left-1/2 w-[300%] h-[300%] -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                style={{ border: 'none' }}
                allow="autoplay; encrypted-media; accelerometer; gyroscope"
                allowFullScreen={false}
                data-testid="hero-youtube-video"
                title="Background Video"
              />
            </div>
          ) : (
            // Direct video file for MP4/WebM URLs
            <video
              autoPlay
              muted
              loop
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="hero-video"
            >
              <source src={heroVideoUrl} type="video/mp4" />
            </video>
          )
        ) : (
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${heroImageUrl})` }}
            data-testid="hero-image"
          />
        )}
        {/* Overlay for better text readability - opacity and color from CMS */}
        <div 
          className={`absolute inset-0 ${overlayColorClass}`}
          style={{ opacity: heroOverlayOpacity / 100 }}
          data-testid="hero-overlay"
        ></div>
        
        {/* Content with proper spacing */}
        <div className="relative z-10 text-center px-4 py-12 md:py-20">
          <img 
            src={heroLogoUrl} 
            alt="Happy Retreats" 
            className="h-24 sm:h-28 md:h-36 lg:h-40 mx-auto mb-4 md:mb-6 object-contain drop-shadow-lg"
            data-testid="hero-logo"
          />
          <h1 className={`text-xl sm:text-2xl md:text-3xl lg:text-4xl font-medium ${titleColorClass} mb-3 font-serif max-w-3xl mx-auto`}>
            {getContent("hero_title", "Start by checking if KAI works for your guest size, stay, and budget.")}
          </h1>
          <p className={`text-sm md:text-base ${subtitleColorClass} max-w-2xl mx-auto`}>
            {getContent("hero_subtitle", "Happy Retreats shares real costs upfront because your celebration deserves clarity, not confusion.")}
          </p>
        </div>
      </section>

      {/* Quick Venue Snapshot Section */}
      <section className="bg-[#F9F6F2] border-b border-[#E8E2DA]" data-testid="venue-snapshot-section">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <h3 className="text-sm font-medium text-[#8B7355] text-center mb-4 tracking-wide">
            {getContent("snapshot_heading", "Quick Venue Snapshot")}
          </h3>
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12">
            {/* Location */}
            <div className="flex items-center gap-2 text-[#4A4A4A]" data-testid="snapshot-location">
              <span className="text-base">📍</span>
              <div className="text-center md:text-left">
                <span className="text-xs text-[#8B7355] block">{getContent("snapshot_location_label", "Location")}</span>
                <span className="text-sm font-medium">{getContent("snapshot_location_value", "Chikkabhallapura, Karnataka")}</span>
              </div>
            </div>
            
            {/* Divider - hidden on mobile */}
            <div className="hidden md:block w-px h-8 bg-[#D4C9BC]"></div>
            
            {/* Weddings Hosted */}
            <div className="flex items-center gap-2 text-[#4A4A4A]" data-testid="snapshot-weddings">
              <span className="text-base">💍</span>
              <div className="text-center md:text-left">
                <span className="text-xs text-[#8B7355] block">{getContent("snapshot_weddings_label", "Weddings Hosted")}</span>
                <span className="text-sm font-medium">
                  {getContent("snapshot_weddings_count", "41")}
                  {getContent("snapshot_weddings_suffix", "as of today") && (
                    <span className="text-xs text-[#8B7355] ml-1">({getContent("snapshot_weddings_suffix", "as of today")})</span>
                  )}
                </span>
              </div>
            </div>
            
            {/* Divider - hidden on mobile */}
            <div className="hidden md:block w-px h-8 bg-[#D4C9BC]"></div>
            
            {/* Private Socials Hosted */}
            <div className="flex items-center gap-2 text-[#4A4A4A]" data-testid="snapshot-socials">
              <span className="text-base">🥂</span>
              <div className="text-center md:text-left">
                <span className="text-xs text-[#8B7355] block">{getContent("snapshot_socials_label", "Private Socials Hosted")}</span>
                <span className="text-sm font-medium">{getContent("snapshot_socials_count", "35")}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="calculator-container py-12 md:py-20" data-testid="calculator-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Input Panel */}
          <div className="lg:col-span-7 space-y-8">
            
            {/* About Happy Retreats - KAI Section */}
            <div className="breakdown-card bg-gradient-to-br from-[#FFFCF8] to-[#F5EDE4]" data-testid="about-kai-section">
              <h2 className="text-2xl font-medium font-serif text-[#2D2D2D] mb-4">
                {getContent("about_title", "About Happy Retreats – KAI")}
              </h2>
              <div className="prose prose-sm text-[#4A4A4A] space-y-4">
                <p>
                  {getContent("about_paragraph_1", "Designed for intimate celebrations, KAI offers four spacious private pool villas with five bedrooms each, along with thoughtfully designed indoor and outdoor spaces that flow naturally through the festivities. At the heart of it all is our floating mantapa, a setting couples fall in love with for how peaceful and personal it feels.")}
                </p>
                <p>
                  {getContent("about_paragraph_2", "KAI offers 20 well-appointed rooms that comfortably host up to 60 guests on beds.")}
                </p>
                <p>
                  {getContent("about_paragraph_3", "For larger celebrations, we thoughtfully accommodate additional guests with extra mattresses, ensuring everyone stays together under one roof, just like family should during a wedding.")}
                </p>
              </div>
              
              {/* Call CTA Button */}
              <div className="mt-6 pt-6 border-t border-[#E8E2DA]">
                <a 
                  href={`tel:${getContent("cta_phone_number", "+919876543210").replace(/\s/g, '')}`}
                  className="inline-flex items-center justify-center gap-3 w-full sm:w-auto px-8 py-4 bg-[#B08968] hover:bg-[#9A7858] text-white font-medium rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                  data-testid="call-cta-about"
                  onClick={() => trackCTA("call-cta-about", getContent("cta_call_button_text", "Call Us Now"), "phone_call")}
                >
                  <Phone className="w-5 h-5" />
                  {getContent("cta_call_button_text", "Call Us Now")}
                </a>
              </div>
            </div>

            {/* Availability Checker - Above Guest Count */}
            <AvailabilityChecker onAvailabilityChecked={handleAvailabilityChecked} />

            {/* Availability Check Required Message */}
            {!hasCheckedAvailability && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                <p className="text-amber-800 text-sm">
                  ☝️ Please check date availability above to proceed with the budget calculator
                </p>
              </div>
            )}

            {/* Guest Count - Show after availability check */}
            <div className={`breakdown-card transition-all duration-300 ${!hasCheckedAvailability ? 'opacity-50 pointer-events-none' : ''}`} data-testid="guest-count-section">
              <div className="flex items-center gap-3 mb-6">
                <Users className="w-5 h-5 text-[#B08968]" />
                <h2 className="text-xl font-medium font-serif">Guest Count</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Tell us roughly how many guests you're planning for
              </p>
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  data-testid="guest-count-input"
                  value={guestCount}
                  onChange={(e) => setGuestCount(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-32 text-center text-xl h-14"
                  min={1}
                />
                <span className="text-muted-foreground">guests</span>
              </div>
              {guestCount > 100 ? (
                <p className="text-sm mt-3" style={{ color: '#800020' }}>
                  That's a lovely guest list. KAI is best experienced with up to 100 guests, and if you're flexible to adjust we are happy to accommodate as well.
                </p>
              ) : (
                <p className="text-sm text-green-600 mt-3">
                  {getContent("guest_count_extra_note", "KAI is ideal for celebrations of up to 60 guests across four villas. If your guest list grows, we'll happily arrange extra mattresses at a minimal additional cost.")}
                </p>
              )}
            </div>

            {/* Duration Selection */}
            <div className="breakdown-card" data-testid="duration-section">
              <div className="flex items-center gap-3 mb-6">
                <Clock className="w-5 h-5 text-[#B08968]" />
                <h2 className="text-xl font-medium font-serif">Venue Duration</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                How long would you like to make KAI yours? (All venue prices are inclusive of GST)
              </p>
              <div className="grid grid-cols-3 gap-4">
                {[24, 48, 72].map((hours) => (
                  <button
                    key={hours}
                    data-testid={`duration-${hours}hr`}
                    onClick={() => {
                      setDurationHours(hours);
                      trackEvent("duration_selected", { hours, days: hours / 24 });
                    }}
                    className={`duration-option ${durationHours === hours ? 'selected border-[#B08968] bg-[#B08968]/5' : 'border-gray-200 hover:border-[#B08968]/50'}`}
                  >
                    <div className="text-center">
                      <span className="text-2xl font-semibold text-[#2D2D2D]">{hours}</span>
                      <span className="block text-sm text-muted-foreground">hours</span>
                      <span className="block text-xs text-muted-foreground mt-1">
                        ({hours / 24} {hours === 24 ? 'day' : 'days'})
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Food Package Toggle & Selection */}
            <div className="breakdown-card" data-testid="food-section">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <UtensilsCrossed className="w-5 h-5 text-[#B08968]" />
                  <h2 className="text-xl font-medium font-serif">Food Package</h2>
                </div>
                <div className="flex items-center gap-3">
                  <Label htmlFor="include-food" className="text-sm text-muted-foreground">
                    Include Food?
                  </Label>
                  <Switch
                    id="include-food"
                    data-testid="include-food-toggle"
                    checked={includeFood}
                    onCheckedChange={setIncludeFood}
                  />
                </div>
              </div>
              
              {includeFood ? (
                <>
                  <div className="text-sm text-muted-foreground mb-4 space-y-3">
                    <p>
                      Rates are calculated per person, per day, and scale with the duration you choose. All prices include GST, and the per-head, per-day cost covers all meals during your stay (lunch, high tea, dinner, and breakfast).
                    </p>
                    <p>
                      Not sure about final numbers yet? That's perfectly okay. Start with a minimum guaranteed count, and we'll help you refine the details as your plans come together.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {foodTiers.map((tier) => (
                      <button
                        key={tier.id}
                        data-testid={`food-tier-${tier.min_guarantee}`}
                        onClick={() => setSelectedFoodTier(tier.id)}
                        className={`tier-card text-left ${
                          selectedFoodTier === tier.id ? 'selected' : ''
                        }`}
                      >
                        <div className="font-semibold text-[#2D2D2D] mb-2">{tier.name}</div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p>Min guarantee: {tier.min_guarantee} pax</p>
                          <p className="font-medium text-[#B08968]">{formatINR(tier.base_rate)}/head/day</p>
                          <p className="text-xs">Additional: {formatINR(tier.additional_rate)}/head/day</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="bg-secondary/30 rounded-xl p-6 text-center">
                  <p className="text-muted-foreground">
                    Food package not included in this estimate. 
                    <br />
                    <span className="text-sm">Toggle above to include food pricing.</span>
                  </p>
                </div>
              )}
            </div>

            {/* Payment Mode & Discount */}
            <div className="breakdown-card" data-testid="payment-mode-section">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className="w-5 h-5 text-[#B08968]" />
                <h2 className="text-xl font-medium font-serif">Cost Benefits based on your mode of payment</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                A 50% advance confirms your booking. If you pay 75% or 100% upfront, you get a higher discount on the total deal value. You save money.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { value: 50, label: "50% advance", discount: "10% off", isDefault: true },
                  { value: 75, label: "75% upfront", discount: "25% off", isDefault: false },
                  { value: 100, label: "100% upfront", discount: "35% off", isDefault: false }
                ].map((option) => (
                  <button
                    key={option.value}
                    data-testid={`payment-${option.value}`}
                    onClick={() => {
                      setAdvancePayment(option.value);
                      trackEvent("payment_mode_selected", { 
                        advance_percent: option.value, 
                        discount: option.discount 
                      });
                    }}
                    className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                      advancePayment === option.value 
                        ? 'border-[#B08968] bg-[#B08968]/5' 
                        : 'border-gray-200 hover:border-[#B08968]/50'
                    }`}
                  >
                    <div className="text-lg font-semibold text-[#2D2D2D]">{option.label}</div>
                    <div className="text-sm text-green-600 font-medium">{option.discount}</div>
                    {option.isDefault && (
                      <div className="text-xs text-muted-foreground mt-1">(confirms booking)</div>
                    )}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                * Discount is calculated on the Total Deal Value
              </p>
            </div>
          </div>

          {/* Budget Breakdown Panel */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-8 space-y-6">
              
              {/* Active Lock Banner */}
              {activeLock && (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-4" data-testid="active-lock-banner">
                  <div className="flex items-center gap-3 mb-2">
                    <Lock className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-700">Price Locked!</span>
                  </div>
                  <p className="text-sm text-green-600 mb-2">
                    Your price of {formatINR(activeLock.grand_total)} is locked.
                  </p>
                  <div className="flex items-center gap-2 text-sm">
                    <Timer className="w-4 h-4 text-green-600" />
                    <span className="font-medium text-green-700">
                      Time remaining: {formatTimeRemaining(timeRemaining)}
                    </span>
                  </div>
                  <p className="text-xs text-green-600 mt-2">
                    This price is valid only for 48 hours and deals will be accepted on a first come first serve basis.
                  </p>
                </div>
              )}

              <div className="breakdown-card" data-testid="budget-breakdown">
                <h2 className="text-2xl font-medium font-serif text-[#2D2D2D] mb-6">
                  Budget Breakdown
                </h2>
                
                {breakdown ? (
                  <div className="space-y-5">
                    {/* Deal Value Components */}
                    <div className="space-y-3">
                      <h3 className="font-medium text-[#B08968] uppercase text-xs tracking-wider">Deal Value Components</h3>
                      
                      {/* Venue */}
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          Venue ({durationHours}hr package, incl. GST)
                        </span>
                        <span className="font-medium">{formatINR(breakdown.venue.base_price)}</span>
                      </div>
                      
                      {/* Food */}
                      {breakdown.food.included ? (
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Food: {breakdown.food.tier_name}
                            </span>
                          </div>
                          <div className="flex justify-between text-xs text-muted-foreground pl-4">
                            <span>
                              {breakdown.food.min_guarantee} pax × {formatINR(breakdown.food.base_rate)}/day × {breakdown.food.days} days
                            </span>
                          </div>
                          {breakdown.food.additional_pax > 0 && (
                            <div className="flex justify-between text-xs text-muted-foreground pl-4">
                              <span>
                                + {breakdown.food.additional_pax} extra × {formatINR(breakdown.food.additional_rate)}/day × {breakdown.food.days} days
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between text-sm pl-4">
                            <span className="text-muted-foreground">Food Total</span>
                            <span className="font-medium">{formatINR(breakdown.food.total)}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground italic">Food: Not included (customer opted out)</span>
                          <span className="font-medium">₹0</span>
                        </div>
                      )}
                      
                      {/* Extra Accommodation (now part of deal value) */}
                      {breakdown.venue.extra_accommodation > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">
                            Extra Stay ({breakdown.venue.extra_pax} pax × ₹1,000 × {breakdown.venue.days} days)
                          </span>
                          <span className="font-medium">{formatINR(breakdown.venue.extra_accommodation)}</span>
                        </div>
                      )}
                      
                      {/* Deal Value Total */}
                      <div className="flex justify-between font-semibold pt-2 border-t border-dashed">
                        <span>Total Deal Value</span>
                        <span data-testid="deal-value">{formatINR(breakdown.deal_value)}</span>
                      </div>
                    </div>

                    {/* Discount Section */}
                    <div className="space-y-2 bg-green-50 rounded-lg p-4">
                      <h3 className="font-medium text-green-700 uppercase text-xs tracking-wider">Your Savings</h3>
                      <div className="flex justify-between text-sm">
                        <span className="text-green-700">
                          Payment mode: {getPaymentModeLabel(breakdown.discount.payment_mode)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-green-700">
                          Discount ({breakdown.discount.percent}% on Deal Value)
                        </span>
                        <span className="font-semibold text-green-700">
                          -{formatINR(breakdown.discount.amount)}
                        </span>
                      </div>
                    </div>

                    {/* Discount Saved Popup - Shows above Grand Total for 75% and 100% payment modes */}
                    {showDiscountPopup && (advancePayment === 75 || advancePayment === 100) && (
                      <div 
                        className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 animate-in fade-in slide-in-from-top-2 duration-300"
                        data-testid="discount-popup"
                      >
                        <div className="flex items-center justify-center gap-2">
                          <div className="text-center">
                            <p className="text-xl font-bold text-green-700 animate-pulse">
                              {formatINR(animatedDiscount)} saved
                            </p>
                            <p className="text-sm text-green-600">
                              Unlocked with upfront payment
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Grand Total */}
                    <div className={`pt-4 border-t-2 border-[#B08968]/30 transition-all duration-300 ${showDiscountPopup ? 'bg-gradient-to-r from-green-50/30 to-transparent -mx-2 px-2 rounded-lg' : ''}`}>
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-medium">Estimated Total</span>
                        <span className={`total-display transition-all duration-300 ${showDiscountPopup ? 'scale-105 text-green-700' : ''}`} data-testid="grand-total">
                          {formatINR(breakdown.grand_total)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        * Approximate estimate. All prices inclusive of GST. Final pricing may vary.
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-3 pt-4">
                      {/* Google User Status & Logout */}
                      {googleUser && (
                        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-white text-sm font-medium">
                              {googleUser.name?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-green-800">{googleUser.name}</p>
                              <p className="text-xs text-green-600">{googleUser.email}</p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleGoogleSignOut}
                            className="text-green-700 hover:text-red-600 hover:bg-red-50"
                          >
                            <LogOut className="w-4 h-4 mr-1" />
                            Logout
                          </Button>
                        </div>
                      )}

                      {/* Lock Deal Button */}
                      {!activeLock && (
                        googleUser ? (
                          <Button
                            data-testid="lock-deal-btn"
                            onClick={handleLockDeal}
                            disabled={lockingDeal}
                            className="w-full bg-green-600 hover:bg-green-700 text-white rounded-full py-6 text-lg font-medium transition-all duration-300"
                          >
                            {lockingDeal ? (
                              "Locking..."
                            ) : (
                              <>
                                <Lock className="w-5 h-5 mr-2" />
                                Lock this deal for 48 hours
                              </>
                            )}
                          </Button>
                        ) : (
                          <div className="bg-secondary/50 rounded-xl p-4 text-center">
                            <p className="text-sm text-muted-foreground mb-3">
                              Sign in with Google to lock this price for 48 hours
                            </p>
                            <Button
                              onClick={handleGoogleSignIn}
                              variant="outline"
                              className="gap-2"
                            >
                              <LogIn className="w-4 h-4" />
                              Sign in with Google
                            </Button>
                          </div>
                        )
                      )}

                      {/* Download PDF Button */}
                      <Button
                        data-testid="download-pdf-btn"
                        onClick={handleExportPdf}
                        disabled={generatingPdf}
                        className="w-full bg-[#B08968] hover:bg-[#9A7558] text-white rounded-full py-6 text-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
                      >
                        {generatingPdf ? (
                          "Generating PDF..."
                        ) : (
                          <>
                            <Download className="w-5 h-5 mr-2" />
                            Download PDF
                          </>
                        )}
                      </Button>

                      {/* Terms & Conditions */}
                      <div className="mt-6 pt-4 border-t border-border/50">
                        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                          Terms & Conditions
                        </h4>
                        <ul className="text-xs text-muted-foreground space-y-1.5 leading-relaxed">
                          <li>• This estimate is indicative and based on the selections made.</li>
                          <li>• All prices are inclusive of GST unless stated otherwise.</li>
                          <li>• Discounts are applied as per the selected payment mode and exclude extra accommodation charges.</li>
                          <li>• Prices may change unless the deal is locked for 48 hours through the platform.</li>
                          <li>• A booking is confirmed only upon receipt of the required advance and written confirmation from Happy Retreats.</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>Select your preferences to see the breakdown</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder's Note Section */}
      <section className="py-16 bg-gradient-to-br from-[#FFFCF8] to-[#F5EDE4]">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center">
            <h2 className="text-2xl md:text-3xl font-serif font-medium text-[#2D2D2D] mb-8">
              {getContent("founder_note_title", "A note from the founder")}
            </h2>
            
            <div className="text-center space-y-5 text-[#4A4A4A] leading-relaxed italic">
              <p className="text-lg text-[#2D2D2D]">
                {getContent("founder_note_opening", "Your wedding is not just a celebration. It's your parents' dream coming to life.")}
              </p>
              
              <p>
                {getContent("founder_note_para1", "At KAI, we understand the weight of that trust. For these few days, we don't act like a venue team — we step in as your extended family. The ones who stay calm, think ahead, and quietly take responsibility so your parents can breathe easy and actually enjoy the wedding.")}
              </p>
              
              <p>
                {getContent("founder_note_para2", "We keep things simple, transparent, and unrushed. We take on fewer weddings because we believe presence matters more than volume.")}
              </p>
              
              <p className="text-[#B08968] font-medium">
                {getContent("founder_note_quote", "Parents often tell us, \"It felt like family was taking care of us.\"")}
              </p>
              
              <p>
                {getContent("founder_note_para3", "That's the feeling we work towards every single time.")}
              </p>
              
              <p>
                {getContent("founder_note_para4", "Thank you for trusting us with such an important moment. If you need reassurance at any point, just reach out. Family is always close by.")}
              </p>
              
              <div className="pt-6">
                <p className="text-[#2D2D2D] font-medium not-italic">{getContent("founder_note_closing", "Wishing you all the happiness,")}</p>
                <p className="text-xl font-serif font-medium text-[#B08968] mt-2 not-italic">{getContent("founder_name", "Pradeep Gowriraju")}</p>
                <p className="text-sm text-muted-foreground not-italic">{getContent("founder_title", "Founder & CEO")}</p>
                <p className="text-sm text-muted-foreground not-italic">{getContent("founder_company", "Happy Retreats")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Performance & Revenue Charts Section */}
      {showCharts && (monthlyPerformance.length > 0 || revenueCategories.length > 0) && (
        <section className="py-16 bg-white" data-testid="performance-section">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2D2D2D] mb-3">
                {getContent("charts_title", "Performance & Revenue Mix at KAI")}
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {getContent("charts_subtitle", "Every booking, every celebration, and every recommendation has played a role in this growth. If you're here today, you're already part of that story.")}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Monthly Performance Chart - Horizontal Bar with Scroll */}
              {monthlyPerformance.length > 0 && (
                <div className="bg-[#FAFAFA] rounded-2xl p-6 shadow-sm">
                  <h3 className="text-lg font-medium text-[#2D2D2D] mb-1">
                    {getContent("charts_performance_title", "Monthly Performance at KAI")}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-6">
                    {getContent("charts_performance_subtitle", "Real bookings since launch")}
                  </p>
                  
                  {/* Scrollable container - max height 350px, each bar needs ~40px */}
                  <div 
                    className="overflow-y-auto scrollbar-thin scrollbar-thumb-[#B08968]/30 scrollbar-track-transparent hover:scrollbar-thumb-[#B08968]/50"
                    style={{ maxHeight: 350 }}
                  >
                    {/* Dynamic height: 40px per bar, minimum 250px */}
                    <div style={{ width: '100%', height: Math.max(250, monthlyPerformance.length * 40) }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={monthlyPerformance}
                          layout="vertical"
                          margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                        >
                          <XAxis 
                            type="number" 
                            tickFormatter={(value) => `₹${(value / 100000).toFixed(0)}L`}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: '#888', fontSize: 12 }}
                          />
                          <YAxis 
                            type="category" 
                            dataKey="month" 
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: '#4A4A4A', fontSize: 12 }}
                            width={75}
                          />
                          <Tooltip 
                            formatter={(value) => [`₹${value.toLocaleString('en-IN')}`, 'Revenue']}
                            contentStyle={{ 
                              backgroundColor: '#fff', 
                              border: '1px solid #eee',
                              borderRadius: '8px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                          />
                          <Bar 
                            dataKey="amount" 
                            radius={[0, 4, 4, 0]}
                            animationDuration={1000}
                          >
                            {monthlyPerformance.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={index % 2 === 0 ? '#B08968' : '#C9A987'} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}

              {/* Revenue Diversification Chart - Donut */}
              {revenueCategories.length > 0 && (
                <div className="bg-[#FAFAFA] rounded-2xl p-6 shadow-sm">
                  <h3 className="text-lg font-medium text-[#2D2D2D] mb-1">Revenue Mix by Celebration Type</h3>
                  <p className="text-sm text-muted-foreground mb-6">Distribution across event categories</p>
                  
                  <div style={{ width: '100%', height: 300 }} className="flex items-center justify-center">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={revenueCategories}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="amount"
                          nameKey="category"
                          animationDuration={1000}
                          label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                          labelLine={{ stroke: '#888', strokeWidth: 1 }}
                        >
                          {revenueCategories.map((entry, index) => {
                            const colors = ['#B08968', '#C9A987', '#8B7355', '#D4C4B0'];
                            return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                          })}
                        </Pie>
                        <Tooltip 
                          formatter={(value, name) => {
                            const total = revenueCategories.reduce((sum, item) => sum + item.amount, 0);
                            const percent = ((value / total) * 100).toFixed(1);
                            return [`₹${value.toLocaleString('en-IN')} (${percent}%)`, name];
                          }}
                          contentStyle={{ 
                            backgroundColor: '#fff', 
                            border: '1px solid #eee',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Legend */}
                  <div className="flex flex-wrap justify-center gap-4 mt-4">
                    {revenueCategories.map((entry, index) => {
                      const colors = ['#B08968', '#C9A987', '#8B7355', '#D4C4B0'];
                      const total = revenueCategories.reduce((sum, item) => sum + item.amount, 0);
                      const percent = ((entry.amount / total) * 100).toFixed(0);
                      return (
                        <div key={entry.category} className="flex items-center gap-2 text-sm">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: colors[index % colors.length] }}
                          />
                          <span className="text-[#4A4A4A]">{entry.category}</span>
                          <span className="text-muted-foreground">₹{(entry.amount / 100000).toFixed(1)}L ({percent}%)</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Charts Footer Text */}
            <div className="text-center mt-10 max-w-3xl mx-auto">
              <p className="text-[#4A4A4A] italic">
                {getContent("charts_footer", "We aim to host 60–80 intimate weddings every year at KAI. That's not a volume goal, it's a promise to keep celebrations personal, calm, and well cared for.")}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* YouTube Videos Section */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2D2D2D] mb-3">
              {getContent("youtube_title", "See KAI in action")}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {getContent("youtube_subtitle", "Real celebrations, real moments, and honest walkthroughs to help you visualise your wedding at KAI.")}
            </p>
          </div>

          {/* Video Grid or Playlist Embed */}
          {youtubeVideos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {youtubeVideos.map((video) => (
                <div
                  key={video.id}
                  className="group relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer bg-white"
                  onClick={() => setActiveVideo(video)}
                  data-testid={`video-card-${video.id}`}
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-video">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-white/90 group-hover:bg-white group-hover:scale-110 transition-all duration-300 flex items-center justify-center shadow-lg">
                        <Play className="w-7 h-7 text-[#B08968] ml-1" fill="#B08968" />
                      </div>
                    </div>
                  </div>
                  {/* Title */}
                  <div className="p-4">
                    <h3 className="font-medium text-[#2D2D2D] group-hover:text-[#B08968] transition-colors line-clamp-2">
                      {video.title}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Fallback: Embed the entire playlist
            <div className="max-w-3xl mx-auto mb-8">
              <div className="aspect-video rounded-xl overflow-hidden shadow-lg">
                <iframe
                  src={`https://www.youtube.com/embed/videoseries?list=${playlistId}&rel=0&modestbranding=1`}
                  title="KAI Wedding Venue Playlist"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>
          )}

          {/* View Full Playlist Link */}
          <div className="text-center">
            <a
              href={`https://youtube.com/playlist?list=${playlistId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#B08968] hover:text-[#9A7558] font-medium transition-colors"
              data-testid="view-playlist-link"
            >
              View full playlist on YouTube
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Instagram Reels Section */}
      {instagramReels.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2D2D2D] mb-3">
                Follow us on Instagram
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Catch the latest moments, behind-the-scenes, and celebrations at KAI.
              </p>
            </div>

            {/* Reels Grid - Consistent card layout */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {instagramReels.map((reel) => (
                <a
                  key={reel.id}
                  href={reel.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative block rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 bg-white border border-gray-100"
                  data-testid={`reel-card-${reel.id}`}
                >
                  {/* Fixed square aspect ratio for consistency */}
                  <div className="aspect-square relative overflow-hidden bg-gray-100">
                    <iframe
                      src={`https://www.instagram.com/p/${reel.id}/embed/captioned`}
                      className="absolute w-[200%] h-[200%] border-0 pointer-events-none"
                      style={{ 
                        top: '-25%', 
                        left: '-50%',
                        transform: 'scale(0.5)',
                        transformOrigin: 'center center'
                      }}
                      loading="lazy"
                      title={reel.title}
                      scrolling="no"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
                    
                    {/* Play/View indicator */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-white/90 group-hover:bg-white group-hover:scale-110 transition-all duration-300 flex items-center justify-center shadow-lg">
                        <Instagram className="w-8 h-8 text-pink-600" />
                      </div>
                    </div>
                    
                    {/* Bottom label */}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <p className="text-white text-sm font-medium truncate">{reel.title}</p>
                      <p className="text-white/80 text-xs">View on Instagram</p>
                    </div>
                  </div>
                </a>
              ))}
            </div>

            {/* Explore Gallery Button */}
            <div className="text-center space-y-4">
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {getContent("gallery_description", "For a clear picture of KAI, visit our website. We've shared almost everything you'll need to imagine your wedding here.")}
              </p>
              
              <div>
                <a
                  href="https://happyretreats.in/kai-wedding-venue"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#B08968] hover:bg-[#9A7558] text-white px-8 py-3 rounded-full font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
                  data-testid="explore-gallery-btn"
                >
                  {getContent("gallery_button_text", "Explore the KAI Gallery")}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer with Call CTA */}
      <footer className="bg-[#2D2D2D] text-white py-12" data-testid="footer-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <img 
            src={heroLogoUrl} 
            alt="Happy Retreats" 
            className="h-16 mx-auto mb-6 object-contain brightness-0 invert opacity-80"
          />
          <p className="text-gray-400 text-sm mb-6 max-w-md mx-auto">
            Creating unforgettable celebrations at KAI. Your wedding, our family's honor.
          </p>
          
          {/* Call CTA Button */}
          <a 
            href={`tel:${getContent("cta_phone_number", "+919876543210").replace(/\s/g, '')}`}
            className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#B08968] hover:bg-[#9A7858] text-white font-medium rounded-lg transition-all duration-200 shadow-md hover:shadow-lg mb-6"
            data-testid="call-cta-footer"
            onClick={() => trackCTA("call-cta-footer", getContent("cta_call_button_text", "Call Us Now"), "phone_call")}
          >
            <Phone className="w-5 h-5" />
            {getContent("cta_call_button_text", "Call Us Now")}
          </a>
          
          <div className="border-t border-gray-700 pt-6 mt-6">
            <p className="text-gray-500 text-xs">
              © {new Date().getFullYear()} Happy Retreats. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      {activeVideo && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setActiveVideo(null)}
          data-testid="video-modal"
        >
          <div 
            className="relative w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setActiveVideo(null)}
              className="absolute -top-12 right-0 text-white hover:text-[#B08968] transition-colors"
              data-testid="close-video-modal"
            >
              <X className="w-8 h-8" />
            </button>
            
            {/* Video Player */}
            <div className="aspect-video bg-black rounded-lg overflow-hidden">
              <iframe
                src={`https://www.youtube.com/embed/${activeVideo.id}?autoplay=0&rel=0&modestbranding=1`}
                title={activeVideo.title}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            
            {/* Video Title */}
            <h3 className="text-white text-lg font-medium mt-4 text-center">
              {activeVideo.title}
            </h3>
          </div>
        </div>
      )}

      {/* Lead Capture Modal */}
      <Dialog open={showLeadModal} onOpenChange={setShowLeadModal}>
        <DialogContent className="sm:max-w-md" data-testid="lead-capture-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Save Your Estimate</DialogTitle>
            <DialogDescription>
              We'll send you the detailed breakdown and get in touch to help plan your perfect day.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleLeadSubmit} className="space-y-4 mt-4">
            <div className="form-group">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                data-testid="lead-name-input"
                required
                value={leadForm.name}
                onChange={(e) => setLeadForm({...leadForm, name: e.target.value})}
                placeholder="Your full name"
              />
            </div>
            
            <div className="form-group">
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                data-testid="lead-phone-input"
                required
                type="tel"
                value={leadForm.phone}
                onChange={(e) => setLeadForm({...leadForm, phone: e.target.value})}
                placeholder="+91 98765 43210"
              />
            </div>
            
            <div className="form-group">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                data-testid="lead-email-input"
                required
                type="email"
                value={leadForm.email}
                onChange={(e) => setLeadForm({...leadForm, email: e.target.value})}
                placeholder="your@email.com"
              />
            </div>
            
            <div className="form-group">
              <Label htmlFor="event_date">Event Date (Optional)</Label>
              <Input
                id="event_date"
                data-testid="lead-date-input"
                type="date"
                value={leadForm.event_date}
                onChange={(e) => setLeadForm({...leadForm, event_date: e.target.value})}
              />
            </div>
            
            <div className="form-group">
              <Label htmlFor="notes">Additional Notes</Label>
              <Input
                id="notes"
                data-testid="lead-notes-input"
                value={leadForm.notes}
                onChange={(e) => setLeadForm({...leadForm, notes: e.target.value})}
                placeholder="Any special requirements..."
              />
            </div>
            
            <div className="bg-secondary/50 rounded-lg p-4 mt-4">
              <p className="text-sm font-medium">Your Estimate Summary</p>
              <p className="text-2xl font-bold text-[#B08968] font-serif">
                {breakdown && formatINR(breakdown.grand_total)}
              </p>
              {breakdown && breakdown.discount.amount > 0 && (
                <p className="text-xs text-green-600">
                  You save {formatINR(breakdown.discount.amount)} with {getPaymentModeLabel(breakdown.discount.payment_mode)}
                </p>
              )}
            </div>
            
            <Button
              type="submit"
              data-testid="submit-lead-btn"
              disabled={submittingLead}
              className="w-full bg-[#B08968] hover:bg-[#9A7558] text-white rounded-full py-6"
            >
              {submittingLead ? (
                "Submitting..."
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Submit & Get Quote
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Mobile Sticky Total */}
      {breakdown && (
        <div className="sticky-total lg:hidden flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground">Estimated Total</p>
            <p className="text-xl font-bold text-[#B08968] font-serif">{formatINR(breakdown.grand_total)}</p>
          </div>
          <Button
            onClick={handleExportPdf}
            disabled={generatingPdf}
            className="bg-[#B08968] hover:bg-[#9A7558] text-white rounded-full px-6"
          >
            {generatingPdf ? "..." : "Download PDF"}
          </Button>
        </div>
      )}

      {/* Chat Widget */}
      <ChatWidget />
    </div>
  );
};

export default WeddingsPage;
